import React, { useState, useRef } from 'react';
import { theme, card, btnPrimary, btnSecondary, input } from '../theme';
// v0.50.0 — lucide icon replaces emoji glyph in drop zone.
import { Paperclip } from 'lucide-react';
import ImportFromInSiteModal from './ImportFromInSiteModal';
// 2026-08-20 — pinned-plan delete guard (Midland close-out); see handleDelete.
import { isPinConflict, unpinConfirmMessage } from '../utils/plan-delete-guard.js';

/**
 * PlansManagerModal — v0.34.0.
 *
 * Opens when the user clicks the "Drop a Plan" pill from MottoButtons
 * (or any other entry point that wants plan management UX).
 *
 * Contents:
 *   1. Drop area at the top — drag PDFs/images here, or click to pick.
 *      Multi-file aware; hands the file list back to onAcceptFiles so
 *      the parent's existing planQueue + PlanRenameModal pipeline
 *      handles the rename + upload (we don't duplicate the conversion
 *      logic here).
 *   2. List of existing plans below — one row per drawing showing:
 *        • a 32-px thumbnail (raster preview if available)
 *        • the user-facing nickname (Title), inline-editable on hover
 *        • the original filename below in muted text
 *        • a small delete button
 *
 * Props:
 *   drawings        []                  — current drawings array
 *   onAcceptFiles   (files: File[]) => void   — pass to parent's queue
 *   onRename        (id, name) => Promise     — PATCH the drawing's Title
 *   onDelete        (id) => Promise           — DELETE the drawing
 *   onClose         () => void
 */
export default function PlansManagerModal({
  drawings = [],
  onAcceptFiles,
  onRename,
  onDelete,
  onClose,
  projectId,
  reportId,
  onImported,
}) {
  const [dropHover, setDropHover] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);

  async function commitRename(d) {
    const next = (editText || '').trim();
    if (!next || next === (d.Title || d.name)) {
      setEditingId(null);
      return;
    }
    setBusyId(d.Id || d.id); setErr(null);
    try {
      await onRename(d.Id || d.id, next);
      setEditingId(null);
    } catch (e) {
      setErr(e.message || 'Rename failed');
    } finally {
      setBusyId(null);
    }
  }

  // 2026-08-20 — Midland close-out. The old confirm here said the file "just
  // stops appearing in this report's plan list", which was false: the DELETE
  // deletes the drawing and strips the pin off every defect on it,
  // unrecoverably — that is how Brett's 25 pins died on 14 Aug. The server now
  // answers 409 with the true pin count when a plan has pins (see
  // api/drawings/index.js); we relay that sentence and only retry with the
  // explicit acknowledgement if the user still says yes. Decision logic lives
  // in src/utils/plan-delete-guard.js (tested).
  async function handleDelete(d) {
    const label = d.Title || d.name || `Drawing #${d.Id || d.id}`;
    if (!confirm(`Delete "${label}" from this report?\n\nItems pinned on it keep their details but lose their place on the plan.`)) return;
    setBusyId(d.Id || d.id); setErr(null);
    try {
      await onDelete(d.Id || d.id);
    } catch (e) {
      if (isPinConflict(e)) {
        // The guard spoke: the plan has pins. Put the server's true count in
        // front of the user and only then retry with the acknowledgement.
        if (confirm(unpinConfirmMessage(e))) {
          try {
            await onDelete(d.Id || d.id, { unpin: true });
          } catch (e2) {
            setErr(e2.message || 'Remove failed');
          } finally {
            setBusyId(null);
          }
          return;
        }
        setBusyId(null);
        return;
      }
      setErr(e.message || 'Remove failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.surface, color: theme.text, width: 'min(640px, 100%)', maxHeight: '90vh',
          borderRadius: 12, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Plans</h3>
          <span style={{ fontSize: 12, color: theme.textLight }}>
            {drawings.length} plan{drawings.length === 1 ? '' : 's'} on this report
          </span>
        </div>

        {/* DROP ZONE */}
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDragEnter={(e) => {
            e.preventDefault();
            dragDepthRef.current += 1;
            if (dragDepthRef.current === 1) setDropHover(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setDropHover(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            dragDepthRef.current = 0;
            setDropHover(false);
            const files = [...(e.dataTransfer?.files || [])];
            if (files.length) onAcceptFiles(files);
          }}
          style={{
            margin: 14,
            border: `2px dashed ${theme.brand}`,
            borderRadius: 10,
            padding: '20px 14px',
            background: dropHover ? theme.bg : 'transparent',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 0.12s, box-shadow 0.12s',
            boxShadow: dropHover ? `inset 0 0 0 2px ${theme.brand}` : 'none',
          }}
          title="Drop one or more PDFs / images, or click to pick"
        >
          <div style={{ marginBottom: 4, color: theme.brand }}><Paperclip size={22} /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
            {dropHover ? 'Release to upload' : 'Drag PDFs or images here'}
          </div>
          <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
            …or click to pick. Multiple OK — each gets named one at a time.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf,image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const fs = Array.from(e.target.files || []);
              if (fs.length) onAcceptFiles(fs);
              e.target.value = '';
            }}
          />
        </div>

        <div style={{ padding: '0 14px 12px', display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => setImportOpen(true)} style={{ border: '1px solid ' + theme.brand, background: 'transparent', color: theme.brand, padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Import a plan from InSite</button>
        </div>

        {err && (
          <div style={{ margin: '0 14px 10px', padding: '6px 10px', background: theme.bg, color: theme.danger, border: '1px solid ' + theme.danger, borderRadius: 6, fontSize: 12 }}>
            {err}
          </div>
        )}

        {/* LIST */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 14px 14px' }}>
          {drawings.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: theme.textLight, fontSize: 13 }}>
              No plans on this report yet — drop one above to start.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {drawings.map((d) => {
                const id = d.Id || d.id;
                const isEditing = editingId === id;
                const isBusy = busyId === id;
                const nickname = d.Title || d.name || '(untitled)';
                // v0.38.0 — sourceFileName is the canonical API field now
                //           (PlansManagerModal was reading FileName/fileName
                //           which never actually existed in the response).
                const filename = d.sourceFileName || d.SourceFileName || d.FileName || d.fileName || '';
                const thumb = d.FileDataUrl || d.rasterUrl || d.sourceUrl || null;
                return (
                  <div key={id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px',
                    border: `1px solid ${theme.border}`, borderRadius: 8,
                    opacity: isBusy ? 0.5 : 1,
                  }}>
                    {/* Thumbnail */}
                    <div style={{
                      width: 36, height: 36, borderRadius: 4,
                      background: theme.bg,
                      backgroundImage: thumb ? `url(${thumb})` : 'none',
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      border: `1px solid ${theme.border}`,
                      flexShrink: 0,
                    }} />
                    {/* Name + filename */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <input
                          autoFocus
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(d);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          onBlur={() => commitRename(d)}
                          style={{ ...input, padding: '4px 8px', fontSize: 13, width: '100%' }}
                        />
                      ) : (
                        <div
                          onClick={() => { setEditingId(id); setEditText(nickname); }}
                          title="Click to rename"
                          style={{
                            fontSize: 13, fontWeight: 600, color: theme.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            cursor: 'text',
                          }}
                        >{nickname}</div>
                      )}
                      {filename && (
                        <div style={{
                          fontSize: 11, color: theme.textLight,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                        }}>{filename}</div>
                      )}
                    </div>
                    {/* Remove */}
                    <button
                      onClick={() => handleDelete(d)}
                      disabled={isBusy}
                      title="Remove from this report"
                      style={{
                        background: 'transparent', border: 'none',
                        color: theme.danger, cursor: 'pointer',
                        fontSize: 13, padding: '4px 8px',
                      }}
                    >Remove</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '10px 18px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </div>
    </div>
    {importOpen && (
      <ImportFromInSiteModal
        projectId={projectId}
        reportId={reportId}
        onClose={() => setImportOpen(false)}
        onImported={() => { setImportOpen(false); onImported && onImported(); }}
      />
    )}
    </>
  );
}
